import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router';
import { useAPI } from 'src/api/provider';
import { Button } from 'src/lib/components/Button';
import { TextField } from 'src/lib/components/fields/TextField';
import useForm from 'src/lib/components/form/useForm';
import { buildAuthInstrumentationHeaders } from 'src/routes/Auth/authInstrumentationHeaders';
import Container from '../Auth/components/Container';
import { z } from 'zod/mini';

interface FormValues {
    password: string;
    passwordConfirmation: string;
}

interface RouteParams extends Record<string, string | undefined> {
    userId: string;
    token: string;
}

const ResetPassword = () => {
    const api = useAPI();
    const { t } = useTranslation('auth.resetPassword');
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const params = useParams<RouteParams>();
    const userId = params.userId!;
    const token = params.token!;
    if (isSubmitted) {
        return (
            <Container width={360}>
                <Typography variant="h2" textAlign="center" marginBottom={2}>
                    {t('passwordHasBeenReset')}
                </Typography>
                <Typography variant="body1" textAlign="center" marginBottom={4}>
                    {t('loginWithNewPassword')}
                </Typography>
                <Box>
                    <RouterLink to="/login">
                        <Button kind="filled" color="primary" fullWidth>
                            {t('returnToLogin')}
                        </Button>
                    </RouterLink>
                </Box>
            </Container>
        );
    }
    return (
        <Container width={360}>
            <Typography variant="h2" textAlign="center" marginBottom={2}>
                {t('chooseNewPassword')}
            </Typography>
            <Typography variant="body1" textAlign="center" marginBottom={4}>
                {t('passwordInstructions')}
            </Typography>
            <ResetPasswordForm
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        await api.post(
                            '/client/reset-password/',
                            {
                                userId,
                                token,
                                password: values.password,
                            },
                            undefined,
                            buildAuthInstrumentationHeaders({
                                flow: 'password_reset_submit',
                                includeResetCorrelationId: true,
                            }),
                        );
                        setIsSubmitted(true);
                    } catch (err: any) {
                        enqueueSnackbar(err.message, { variant: 'error' });
                    } finally {
                        setSubmitting(false);
                    }
                }}
                passwordTooShortLabel={t('passwordTooShort')}
                resetPasswordLabel={t('resetPassword')}
                newPasswordLabel={t('newPassword')}
                confirmNewPasswordLabel={t('confirmNewPassword')}
            />
            <Box>
                <RouterLink to="/login">
                    <Button kind="filled" color="secondary" fullWidth>
                        {t('returnToLogin')}
                    </Button>
                </RouterLink>
            </Box>
        </Container>
    );
};

export default ResetPassword;

interface ResetPasswordFormProps {
    onSubmit: (
        values: FormValues,
        helpers: { setSubmitting: (isSubmitting: boolean) => void },
    ) => Promise<void>;
    passwordTooShortLabel: string;
    resetPasswordLabel: string;
    newPasswordLabel: string;
    confirmNewPasswordLabel: string;
}

const ResetPasswordForm = ({
    onSubmit,
    passwordTooShortLabel,
    resetPasswordLabel,
    newPasswordLabel,
    confirmNewPasswordLabel,
}: ResetPasswordFormProps) => {
    const form = useForm<FormValues>({
        validationSchema: z.object({
            password: z
                .string({
                    error: (iss) => (iss.input === undefined ? 'Required' : 'Required'),
                })
                .check(z.minLength(1, 'Required'))
                .check(z.minLength(6, passwordTooShortLabel)),
            passwordConfirmation: z
                .string({
                    error: (iss) => (iss.input === undefined ? 'Required' : 'Required'),
                })
                .check(z.minLength(1, 'Required')),
        }),
        initialValues: {
            password: '',
            passwordConfirmation: '',
        },
        onSubmit,
    });

    const errors = form.submitCount > 0 ? form.errors : {};

    return (
        <form onSubmit={form.handleSubmit}>
            <Box marginBottom={4}>
                <TextField
                    label={newPasswordLabel}
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={form.values.password}
                    onChange={form.handleChange}
                    error={errors.password}
                    autoFocus
                />
            </Box>
            <Box marginBottom={4}>
                <TextField
                    label={confirmNewPasswordLabel}
                    name="passwordConfirmation"
                    type="password"
                    autoComplete="new-password"
                    value={form.values.passwordConfirmation}
                    onChange={form.handleChange}
                    error={errors.passwordConfirmation}
                />
            </Box>
            <Box marginBottom={2}>
                <Button
                    type="submit"
                    kind="filled"
                    color="primary"
                    fullWidth
                    disabled={
                        form.isSubmitting ||
                        !form.values?.password?.trim() ||
                        !form.values?.passwordConfirmation?.trim()
                    }
                >
                    {resetPasswordLabel}
                </Button>
            </Box>
        </form>
    );
};
